import { useEffect, useState } from "react";
import { RefreshCw, PackageOpen, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatINR, apiError } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const STATUSES = ["pending", "processing", "shipped", "delivered"];
const normStatus = (s) => (STATUSES.includes(s) ? s : "pending");

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const PAYMENT_LABELS = {
  paid: "Paid",
  advance_paid: "Advance Paid",
  fully_paid: "Fully Paid",
  pending: "Pending",
  failed: "Failed",
};

const PaymentBadge = ({ status }) => {
  const styles = {
    paid: "border-gold/60 text-gold",
    fully_paid: "border-gold/60 text-gold",
    advance_paid: "border-gold-accent/70 text-gold-accent",
    pending: "border-cream/25 text-cream/50",
    failed: "border-red-400/50 text-red-400",
  };
  return (
    <span
      data-testid="order-payment-badge"
      className={`border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] ${
        styles[status] || styles.pending
      }`}
    >
      {PAYMENT_LABELS[status] || status}
    </span>
  );
};

const OrdersTab = () => {
  const [orders, setOrders] = useState(null);
  const [trackingFor, setTrackingFor] = useState(null);
  const [courierName, setCourierName] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [savingTracking, setSavingTracking] = useState(false);

  const load = () => {
    api
      .get("/admin/orders")
      .then((r) => setOrders(r.data.orders))
      .catch((e) => {
        toast.error(apiError(e, "Could not load orders"));
        setOrders([]);
      });
  };

  useEffect(load, []);

  const updateStatus = async (id, status) => {
    const prev = orders;
    setOrders((list) => list.map((o) => (o.id === id ? { ...o, status } : o)));
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Order marked ${status}`);
    } catch (e) {
      setOrders(prev);
      toast.error(apiError(e, "Could not update status"));
    }
  };

  const markFullyPaid = async (id) => {
    const prev = orders;
    setOrders((list) =>
      list.map((o) => (o.id === id ? { ...o, payment_status: "fully_paid" } : o))
    );
    try {
      await api.patch(`/admin/orders/${id}/payment-status`, { payment_status: "fully_paid" });
      toast.success("Order marked Fully Paid — balance collected on delivery");
    } catch (e) {
      setOrders(prev);
      toast.error(apiError(e, "Could not update payment status"));
    }
  };

  const openTracking = (o) => {
    setTrackingFor(o);
    setCourierName(o.courier_name || "");
    setTrackingNumber(o.tracking_number || "");
  };

  const saveTracking = async () => {
    if (!trackingFor) return;
    setSavingTracking(true);
    try {
      await api.patch(`/admin/orders/${trackingFor.id}/tracking`, {
        courier_name: courierName,
        tracking_number: trackingNumber,
      });
      setOrders((list) =>
        list.map((o) =>
          o.id === trackingFor.id
            ? { ...o, courier_name: courierName.trim(), tracking_number: trackingNumber.trim() }
            : o
        )
      );
      toast.success("Courier details saved — visible on the customer's tracking page");
      setTrackingFor(null);
    } catch (e) {
      toast.error(apiError(e, "Could not save courier details"));
    } finally {
      setSavingTracking(false);
    }
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/40">
          {orders ? `${orders.length} ${orders.length === 1 ? "order" : "orders"}` : "Loading…"}
        </p>
        <button
          data-testid="admin-orders-refresh-button"
          onClick={load}
          className="inline-flex items-center gap-2 border border-cream/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60 transition-colors hover:border-gold hover:text-gold"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="mt-4 overflow-x-auto border border-cream/10" data-testid="admin-orders-table">
        <table className="w-full min-w-[980px] text-left text-sm">
          <thead>
            <tr className="border-b border-gold/20 bg-espresso-surface font-mono text-[9px] uppercase tracking-[0.2em] text-cream/40">
              <th className="px-4 py-3.5 font-medium">Date</th>
              <th className="px-4 py-3.5 font-medium">Customer</th>
              <th className="px-4 py-3.5 font-medium">Items</th>
              <th className="px-4 py-3.5 font-medium">Total</th>
              <th className="px-4 py-3.5 font-medium">Payment</th>
              <th className="px-4 py-3.5 font-medium">Status</th>
              <th className="px-4 py-3.5 font-medium">Courier</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-cream/8">
            {orders === null ? (
              <tr>
                <td colSpan={7} className="px-4 py-14 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-cream/30">
                  Opening the ledger…
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-14 text-center" data-testid="admin-orders-empty">
                  <PackageOpen size={28} strokeWidth={1} className="mx-auto text-gold-muted/50" />
                  <p className="mt-4 font-serif text-xl text-cream/80">No orders yet</p>
                  <p className="mt-2 text-xs text-cream/40">
                    Purchases will appear here the moment a customer checks out.
                  </p>
                </td>
              </tr>
            ) : (
              orders.map((o) => (
                <tr
                  key={o.id}
                  data-testid={`admin-order-row-${o.id}`}
                  className="transition-colors hover:bg-cream/[0.03]"
                >
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-[10px] text-cream/50">
                    {fmtDate(o.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-serif text-base text-cream">{o.customer?.name}</p>
                    <p className="text-xs text-cream/40">
                      {o.customer?.email}
                      {o.customer?.phone ? ` · ${o.customer.phone}` : ""}
                    </p>
                  </td>
                  <td className="max-w-[280px] px-4 py-3 text-xs text-cream/60">
                    {o.items.map((it, i) => (
                      <p key={i} className="truncate">
                        {it.name} × {it.quantity}
                      </p>
                    ))}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-cream">
                    {formatINR(o.total)}
                    {o.discount > 0 && (
                      <p className="mt-0.5 font-mono text-[9px] text-gold-muted">
                        {o.coupon_code} −{formatINR(o.discount)}
                      </p>
                    )}
                    {o.shipping != null && (
                      <p className="font-mono text-[9px] text-cream/30">
                        {o.shipping === 0 ? "free shipping" : `+${formatINR(o.shipping)} ship`}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <PaymentBadge status={o.payment_status} />
                      {o.payment_method === "cod" && (
                        <span
                          data-testid={`order-cod-chip-${o.id}`}
                          className="border border-cream/25 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.2em] text-cream/60"
                        >
                          COD
                        </span>
                      )}
                    </div>
                    {o.payment_method === "cod" && o.payment_status === "advance_paid" && (
                      <>
                        <p className="mt-1.5 font-mono text-[9px] text-cream/40">
                          {formatINR(o.cod_balance_due || 0)} due on delivery
                        </p>
                        <button
                          data-testid={`order-mark-fully-paid-${o.id}`}
                          onClick={() => markFullyPaid(o.id)}
                          className="mt-1.5 border border-gold/40 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-espresso"
                        >
                          Mark Fully Paid
                        </button>
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Select value={normStatus(o.status)} onValueChange={(v) => updateStatus(o.id, v)}>
                      <SelectTrigger
                        data-testid={`order-status-select-${o.id}`}
                        className="h-8 w-32 rounded-none border-cream/15 bg-transparent font-mono text-[9px] uppercase tracking-[0.15em] text-cream/70"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none border-gold/30 bg-espresso-surface text-cream">
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s} data-testid={`order-status-option-${s}`}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`order-tracking-button-${o.id}`}
                      onClick={() => openTracking(o)}
                      className="inline-flex items-center gap-1.5 border border-cream/15 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-cream/60 transition-colors hover:border-gold hover:text-gold"
                    >
                      <Truck size={11} />
                      {o.courier_name ? o.courier_name : "Add"}
                    </button>
                    {o.tracking_number && (
                      <p className="mt-1 font-mono text-[9px] text-cream/30">{o.tracking_number}</p>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!trackingFor} onOpenChange={(open) => !open && setTrackingFor(null)}>
        <DialogContent
          className="rounded-none border-gold/30 bg-espresso-surface text-cream sm:max-w-md"
          data-testid="tracking-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-cream">Courier &amp; Tracking</DialogTitle>
            <DialogDescription className="text-sm text-cream/50">
              Order #{trackingFor?.id?.slice(0, 8).toUpperCase()} — shown on the customer's
              tracking page.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <input
              data-testid="tracking-courier-input"
              value={courierName}
              onChange={(e) => setCourierName(e.target.value)}
              placeholder="Courier name (e.g. Delhivery, BlueDart)"
              className="w-full border border-cream/15 bg-transparent px-4 py-3 text-sm text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-gold"
            />
            <input
              data-testid="tracking-number-input"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="Tracking / AWB number"
              className="w-full border border-cream/15 bg-transparent px-4 py-3 text-sm text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-gold"
            />
            <button
              data-testid="tracking-save-button"
              onClick={saveTracking}
              disabled={savingTracking}
              className="w-full bg-gold py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-espresso transition-all duration-300 hover:bg-gold-accent active:scale-[0.98] disabled:opacity-50"
            >
              {savingTracking ? "Saving…" : "Save Courier Details"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersTab;
